import { ReactNode, useCallback, useEffect, useState } from 'react';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useFormikContext } from 'formik';
import {
  RequiredTabsConfig,
  TabFieldsConfig,
  useTabErrors
} from 'components/navigation/Tabs/useTabErrors';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import {
  BorrowerInsuredFormFieldNames,
  BorrowerInsuredFormValues
} from '../borrowerInsuredForm';
import { BorrowerSurveyLayout } from './BorrowerSurveyLayout';

interface BorrowerBaseLayoutProps {
  children: ReactNode;
  initialTab?: number | string;
}

export const tabFieldsConfig: TabFieldsConfig<BorrowerInsuredFormValues> = {
  1: [
    PersonalInformationFieldNames.Civility,
    PersonalInformationFieldNames.LastName,
    PersonalInformationFieldNames.FirstName,
    PersonalInformationFieldNames.BirthName,
    PersonalInformationFieldNames.BirthCountry,
    PersonalInformationFieldNames.Nationality,
    PersonalInformationFieldNames.BirthDate,
    PersonalInformationFieldNames.Email,
    PersonalInformationFieldNames.Phone,
    PersonalInformationFieldNames.TaxResidenceCountryId,
    PersonalInformationFieldNames.IsPPE,
    PersonalInformationFieldNames.IsFPE,
    AddressFormFieldNames.Address,
    AddressFormFieldNames.AdditionalAddress,
    AddressFormFieldNames.PostalCode,
    AddressFormFieldNames.City,
    AddressFormFieldNames.Country,
    BorrowerInsuredFormFieldNames.Job,
    BorrowerInsuredFormFieldNames.ProspectType,
    PersonalInformationFieldNames.IsFPE
  ],
  2: [BorrowerInsuredFormFieldNames.SurveyForm]
};

export const requiredTabs: RequiredTabsConfig = {
  2: true
};

export const BorrowerBaseLayout = ({ children, initialTab = 1 }: BorrowerBaseLayoutProps) => {
  const [active, setActive] = useState<number | string>(initialTab);
  const { t } = useDynamicTranslation([NsKey.Borrower, NsKey.Common]);
  const { errors, setFieldTouched } = useFormikContext<BorrowerInsuredFormValues>();

  const { getTabNotification, touchFieldsWithErrors, touchFieldsWithErrorsInTab } = useTabErrors(
    errors,
    tabFieldsConfig,
    requiredTabs,
    setFieldTouched
  );

  // Marquer les champs avec erreurs comme touched au montage et quand les erreurs changent
  useEffect(() => {
    touchFieldsWithErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleTabChange = useCallback((tabValue: number | string) => {
    setActive(tabValue);
    touchFieldsWithErrorsInTab(tabValue);
  }, [touchFieldsWithErrorsInTab]);

  return (
    <div id="side">
      <GenericTabs defaultTab={active} onChange={handleTabChange}>
        <GenericTabs.Item
          notification={getTabNotification(1)}
          label={t('COMMON.LABELS.INFORMATIONS')}
          value={1}
        />
        <GenericTabs.Item
          notification={getTabNotification(2)}
          label={t('COMMON.LABELS.SITUATION')}
          value={2}
        />
        <GenericTabs.Content value={1}>{children}</GenericTabs.Content>
        <GenericTabs.Content value={2}>
          <BorrowerSurveyLayout />
        </GenericTabs.Content>
      </GenericTabs>
    </div>
  );
};
